import React, { useCallback, useContext } from 'react';
import { EditorContext } from '../../../../utils/contexts';
import { getGamificationThemeDefaults } from '../GamificationPanel/gamificationThemeDefaults';
import CollapsedPanel from '../../components/CollapsedPanel';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

const DEFAULT_COLORS = [
  'E63946',
  '457B9D',
  '2A9D8F',
  'E9C46A',
  'F4A261',
  '264653',
  '6D6875',
  'B5838D',
  'A8DADC',
  'F1FAEE',
  '1D3557',
  'E07A5F',
];

export default function GamificationRewardsPanel() {
  const { settings, updateSettings } = useContext(EditorContext);

  const gamification = settings.gamification || {
    game_type: 'spin_wheel',
    one_play_per_visitor: true,
    rewards: [],
  };
  const rewards = gamification.rewards || [];
  const gameType = gamification.game_type || 'spin_wheel';
  const isScratchCard = gameType === 'scratch_card';
  const isLootBox = gameType === 'surprise_box';

  const themeDefaults = getGamificationThemeDefaults(settings.theme_id);
  const slotColors = themeDefaults.spin?.segmentColors || DEFAULT_COLORS;

  const maxRewards = isScratchCard ? 6 : 12;
  const minRewards = 2;

  const updateGamification = useCallback(
    (updates) => {
      updateSettings({
        gamification: {
          ...gamification,
          ...updates,
        },
      });
    },
    [gamification, updateSettings]
  );

  const updateReward = (index, field, value) => {
    const newRewards = rewards.map((r, i) =>
      i === index ? { ...r, [field]: value } : r
    );
    updateGamification({ rewards: newRewards });
  };

  const addReward = () => {
    if (rewards.length >= maxRewards) return;
    const newColor = slotColors[rewards.length % slotColors.length];
    const defaultLabel = isScratchCard ? 'New Outcome' : isLootBox ? 'New Prize' : 'New Reward';
    updateGamification({
      rewards: [
        ...rewards,
        { label: defaultLabel, probability: 0, coupon_code: '', color: newColor },
      ],
    });
  };

  const removeReward = (index) => {
    if (rewards.length <= minRewards) return;
    updateGamification({ rewards: rewards.filter((_, i) => i !== index) });
  };

  const totalProbability = rewards.reduce(
    (sum, r) => sum + (Number(r.probability) || 0),
    0
  );
  const probError = rewards.length > 0 && totalProbability !== 100;

  const panelTitle = isScratchCard ? 'Scratch Outcomes' : isLootBox ? 'Box Prizes' : 'Reward Slots';
  const itemLabel = isScratchCard ? 'Outcome' : isLootBox ? 'Box' : 'Slot';

  return (
    <CollapsedPanel title={panelTitle} allowed>
      <Box sx={{ p: 0 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
          }}
        >
          <Typography variant="subtitle2" fontWeight="bold">
            {panelTitle}
          </Typography>
          <Typography
            variant="caption"
            color={probError ? 'error' : 'success.main'}
            fontWeight="bold"
          >
            Total: {totalProbability}%
            {probError ? ' (must equal 100%)' : ' ✓'}
          </Typography>
        </Box>

        {!isScratchCard && rewards.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              height: 10,
              borderRadius: 1,
              overflow: 'hidden',
              mb: 2,
              border: '1px solid #eee',
            }}
          >
            {rewards.map((r, i) => (
              <Tooltip key={i} title={`${r.label}: ${r.probability}%`}>
                <Box
                  sx={{
                    width: `${Number(r.probability) || 0}%`,
                    background: `#${r.color || DEFAULT_COLORS[i % DEFAULT_COLORS.length]}`,
                    transition: 'width 0.2s',
                  }}
                />
              </Tooltip>
            ))}
          </Box>
        )}

        {isScratchCard && rewards.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Each outcome has a probability of being revealed when a visitor scratches the card.
          </Typography>
        )}

        {rewards.map((reward, index) => (
          <Box
            key={index}
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              p: 1.5,
              mb: 1.5,
              background: reward.is_losing ? '#fff8f8' : '#fafafa',
              borderColor: reward.is_losing ? '#ffcccc' : '#e0e0e0',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {!isScratchCard && !isLootBox && (
                  <Box sx={{ position: 'relative', width: 28, height: 28 }}>
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        background: `#${reward.color || slotColors[index % slotColors.length]}`,
                        border: '2px solid #ccc',
                        cursor: 'pointer',
                      }}
                    />
                    <input
                      type="color"
                      value={`#${reward.color || slotColors[index % slotColors.length]}`}
                      onChange={(e) =>
                        updateReward(index, 'color', e.target.value.replace('#', ''))
                      }
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        opacity: 0,
                        cursor: 'pointer',
                        border: 'none',
                        padding: 0,
                      }}
                    />
                  </Box>
                )}
                {isScratchCard && (
                  <Typography variant="caption" sx={{ fontSize: '1rem' }}>
                    {reward.is_losing ? '😔' : '🎉'}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  {itemLabel} {index + 1}
                </Typography>
              </Box>

              <IconButton
                size="small"
                onClick={() => removeReward(index)}
                disabled={rewards.length <= minRewards}
                sx={{ color: 'error.main' }}
              >
                ✕
              </IconButton>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                label="Label"
                value={reward.label || ''}
                onChange={(e) => updateReward(index, 'label', e.target.value)}
                size="small"
                sx={{ flex: 2 }}
                inputProps={{ maxLength: 24 }}
              />
              <TextField
                label="Win %"
                type="number"
                value={reward.probability ?? ''}
                onChange={(e) =>
                  updateReward(index, 'probability', Number(e.target.value))
                }
                size="small"
                sx={{ flex: 1 }}
                inputProps={{ min: 0, max: 100 }}
              />
            </Box>

            <TextField
              label="Coupon Code (optional)"
              value={reward.coupon_code || ''}
              onChange={(e) => updateReward(index, 'coupon_code', e.target.value)}
              size="small"
              fullWidth
              placeholder={
                isScratchCard
                  ? reward.is_losing
                    ? 'No code needed for losing outcome'
                    : 'e.g. SAVE20'
                  : 'e.g. SAVE20 — leave blank for no-reward slot'
              }
              inputProps={{ maxLength: 50 }}
            />

            <Box sx={{ mt: 1 }}>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={!!reward.is_losing}
                  onChange={(e) =>
                    updateReward(index, 'is_losing', e.target.checked)
                  }
                />
                <Typography variant="caption" color="text.secondary">
                  {isScratchCard ? 'Losing outcome (no reward)' : 'Mark as losing slot'}
                </Typography>
              </label>
            </Box>
          </Box>
        ))}

        <Button
          onClick={addReward}
          disabled={rewards.length >= maxRewards}
          variant="outlined"
          size="small"
          fullWidth
          sx={{ mt: 1 }}
        >
          + Add {itemLabel}
        </Button>

        {probError && (
          <Typography
            variant="caption"
            color="error"
            sx={{ mt: 1, display: 'block' }}
          >
            Probabilities must add up to exactly 100%. Currently: {totalProbability}%
          </Typography>
        )}
      </Box>
    </CollapsedPanel>
  );
}
